import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Eraser,
  Minus,
  MousePointer2,
  Pen,
  Plus,
  RotateCcw,
  Square,
  Trash2,
} from 'lucide-react';
import {
  WHITEBOARD_COLOURS,
  WHITEBOARD_STROKE_WIDTHS,
  WhiteboardToolType,
  type Point,
  type WhiteboardPage,
} from '@tutor/shared';
import type { StrokePreviewBroadcast } from '@tutor/realtime';
import { Button, cn } from '@tutor/ui';
import type { LiveCursor } from '../../hooks/useClassroom';

/**
 * The board is drawn in a fixed logical coordinate space and scaled to fit. Two
 * participants on different screen sizes then see identical geometry — sending raw
 * pixel coordinates would put the tutor's diagram in the wrong place on a laptop.
 */
const BOARD_WIDTH = 1600;
const BOARD_HEIGHT = 1000;

const TOOLS = [
  { tool: WhiteboardToolType.PEN, label: 'Pen', icon: Pen },
  { tool: WhiteboardToolType.HIGHLIGHTER, label: 'Highlighter', icon: MousePointer2 },
  { tool: WhiteboardToolType.LINE, label: 'Line', icon: Minus },
  { tool: WhiteboardToolType.RECTANGLE, label: 'Rectangle', icon: Square },
  { tool: WhiteboardToolType.ERASER, label: 'Eraser', icon: Eraser },
] as const;

export interface WhiteboardProps {
  page: WhiteboardPage | null;
  pages: WhiteboardPage[];
  activePageId: string | null;
  cursors: LiveCursor[];
  previews: Map<string, StrokePreviewBroadcast>;
  canDraw: boolean;
  canManagePages: boolean;
  canClear: boolean;
  onStroke: (stroke: {
    id: string;
    pageId: string;
    tool: WhiteboardToolType;
    colour: string;
    width: number;
    opacity: number;
    points: Point[];
  }) => void;
  onPreview: (preview: {
    pageId: string;
    strokeId: string;
    tool: WhiteboardToolType;
    colour: string;
    width: number;
    points: Point[];
  }) => void;
  onCursor?: (point: Point, pageId: string) => void;
  onUndo: () => void;
  onClear: () => void;
  onAddPage: () => void;
  onSelectPage: (pageId: string) => void;
}

function drawStroke(
  ctx: CanvasRenderingContext2D,
  stroke: { tool: string; colour: string; width: number; opacity?: number; points: Point[] },
): void {
  const { points } = stroke;
  if (points.length === 0) return;

  ctx.save();
  ctx.globalAlpha = stroke.tool === WhiteboardToolType.HIGHLIGHTER ? 0.35 : (stroke.opacity ?? 1);
  ctx.strokeStyle = stroke.colour;
  ctx.fillStyle = stroke.colour;
  ctx.lineWidth = stroke.width;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  if (stroke.tool === WhiteboardToolType.ERASER) {
    // Erasing removes pixels rather than painting white, so the grid stays visible.
    ctx.globalCompositeOperation = 'destination-out';
    ctx.lineWidth = stroke.width * 3;
  }

  const first = points[0]!;

  if (stroke.tool === WhiteboardToolType.RECTANGLE && points.length >= 2) {
    const last = points[points.length - 1]!;
    ctx.strokeRect(first.x, first.y, last.x - first.x, last.y - first.y);
    ctx.restore();
    return;
  }

  if (stroke.tool === WhiteboardToolType.LINE && points.length >= 2) {
    const last = points[points.length - 1]!;
    ctx.beginPath();
    ctx.moveTo(first.x, first.y);
    ctx.lineTo(last.x, last.y);
    ctx.stroke();
    ctx.restore();
    return;
  }

  if (points.length === 1) {
    ctx.beginPath();
    ctx.arc(first.x, first.y, ctx.lineWidth / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    return;
  }

  // Quadratic smoothing through midpoints keeps freehand lines from looking faceted.
  ctx.beginPath();
  ctx.moveTo(first.x, first.y);
  for (let i = 1; i < points.length - 1; i += 1) {
    const current = points[i]!;
    const next = points[i + 1]!;
    ctx.quadraticCurveTo(current.x, current.y, (current.x + next.x) / 2, (current.y + next.y) / 2);
  }
  const last = points[points.length - 1]!;
  ctx.lineTo(last.x, last.y);
  ctx.stroke();
  ctx.restore();
}

function drawBackground(ctx: CanvasRenderingContext2D, type: WhiteboardPage['backgroundType']): void {
  ctx.save();
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, BOARD_WIDTH, BOARD_HEIGHT);

  if (type === 'GRID' || type === 'GRAPH') {
    const step = type === 'GRAPH' ? 25 : 50;
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let x = 0; x <= BOARD_WIDTH; x += step) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, BOARD_HEIGHT);
    }
    for (let y = 0; y <= BOARD_HEIGHT; y += step) {
      ctx.moveTo(0, y);
      ctx.lineTo(BOARD_WIDTH, y);
    }
    ctx.stroke();
  } else if (type === 'LINED') {
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let y = 40; y <= BOARD_HEIGHT; y += 40) {
      ctx.moveTo(0, y);
      ctx.lineTo(BOARD_WIDTH, y);
    }
    ctx.stroke();
  }

  ctx.restore();
}

export function Whiteboard({
  page,
  pages,
  activePageId,
  cursors,
  previews,
  canDraw,
  canManagePages,
  canClear,
  onStroke,
  onPreview,
  onCursor,
  onUndo,
  onClear,
  onAddPage,
  onSelectPage,
}: WhiteboardProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [tool, setTool] = useState<WhiteboardToolType>(WhiteboardToolType.PEN);
  const [colour, setColour] = useState<string>(WHITEBOARD_COLOURS[0]);
  const [width, setWidth] = useState<number>(WHITEBOARD_STROKE_WIDTHS[1]);

  const drawingRef = useRef<{ id: string; points: Point[] } | null>(null);
  const lastPreviewRef = useRef(0);

  /** Screen coordinates → the board's fixed logical space. */
  const toBoardPoint = useCallback((event: React.PointerEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * BOARD_WIDTH,
      y: ((event.clientY - rect.top) / rect.height) * BOARD_HEIGHT,
    };
  }, []);

  // Full repaint on every change. At these stroke counts it is imperceptible and far
  // simpler to reason about than incremental damage tracking.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, BOARD_WIDTH, BOARD_HEIGHT);
    drawBackground(ctx, page?.backgroundType ?? 'BLANK');

    for (const stroke of page?.strokes ?? []) drawStroke(ctx, stroke);

    for (const preview of previews.values()) {
      if (preview.pageId !== page?.id) continue;
      drawStroke(ctx, { ...preview, opacity: 0.7 });
    }

    const inProgress = drawingRef.current;
    if (inProgress) {
      drawStroke(ctx, { tool, colour, width, points: inProgress.points });
    }
  }, [page, previews, tool, colour, width]);

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!canDraw || !page) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    drawingRef.current = { id: crypto.randomUUID(), points: [toBoardPoint(event)] };
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!page) return;
    const point = toBoardPoint(event);

    const now = Date.now();
    // Throttled so a fast pen does not flood the socket with ~120 events a second.
    if (onCursor && now - lastPreviewRef.current > 60) onCursor(point, page.id);

    const inProgress = drawingRef.current;
    if (!inProgress || !canDraw) return;

    inProgress.points.push(point);

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx) drawStroke(ctx, { tool, colour, width, points: inProgress.points });

    if (now - lastPreviewRef.current > 60) {
      lastPreviewRef.current = now;
      onPreview({
        pageId: page.id,
        strokeId: inProgress.id,
        tool,
        colour,
        width,
        points: inProgress.points.slice(-40),
      });
    }
  };

  const handlePointerUp = (): void => {
    const inProgress = drawingRef.current;
    drawingRef.current = null;
    if (!inProgress || !page || !canDraw) return;
    if (inProgress.points.length === 0) return;

    onStroke({
      id: inProgress.id,
      pageId: page.id,
      tool,
      colour,
      width,
      opacity: tool === WhiteboardToolType.HIGHLIGHTER ? 0.35 : 1,
      points: inProgress.points,
    });
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-3 py-2">
        <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1">
          {TOOLS.map((entry) => (
            <button
              key={entry.tool}
              type="button"
              disabled={!canDraw}
              onClick={() => setTool(entry.tool)}
              title={entry.label}
              aria-label={entry.label}
              aria-pressed={tool === entry.tool}
              className={cn(
                'rounded-md p-1.5 text-slate-500 transition-colors disabled:opacity-40',
                tool === entry.tool && 'bg-white text-brand-600 shadow-sm',
              )}
            >
              <entry.icon className="h-4 w-4" aria-hidden />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          {WHITEBOARD_COLOURS.map((swatch) => (
            <button
              key={swatch}
              type="button"
              disabled={!canDraw}
              onClick={() => setColour(swatch)}
              aria-label={`Colour ${swatch}`}
              aria-pressed={colour === swatch}
              className={cn(
                'h-6 w-6 rounded-full ring-2 ring-offset-1 transition-transform disabled:opacity-40',
                colour === swatch ? 'ring-slate-900 scale-110' : 'ring-transparent',
              )}
              style={{ backgroundColor: swatch }}
            />
          ))}
        </div>

        <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1">
          {WHITEBOARD_STROKE_WIDTHS.map((size) => (
            <button
              key={size}
              type="button"
              disabled={!canDraw}
              onClick={() => setWidth(size)}
              aria-label={`Stroke width ${size}`}
              aria-pressed={width === size}
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-md disabled:opacity-40',
                width === size && 'bg-white shadow-sm',
              )}
            >
              <span
                className="rounded-full bg-slate-700"
                style={{ width: Math.min(size, 12), height: Math.min(size, 12) }}
              />
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onUndo} disabled={!canDraw} leftIcon={<RotateCcw className="h-4 w-4" aria-hidden />}>
            Undo
          </Button>
          {canClear && (
            <Button variant="ghost" size="sm" onClick={onClear} leftIcon={<Trash2 className="h-4 w-4" aria-hidden />}>
              Clear
            </Button>
          )}
        </div>
      </div>

      <div ref={containerRef} className="relative flex-1 overflow-hidden bg-slate-100 p-3">
        <div className="relative mx-auto h-full" style={{ aspectRatio: `${BOARD_WIDTH} / ${BOARD_HEIGHT}`, maxWidth: '100%' }}>
          <canvas
            ref={canvasRef}
            width={BOARD_WIDTH}
            height={BOARD_HEIGHT}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
            className={cn(
              'whiteboard-canvas h-full w-full rounded-lg border border-slate-200 bg-white shadow-sm',
              !canDraw && 'cursor-not-allowed',
            )}
          />

          {/* Remote cursors, positioned in the same logical space as the strokes. */}
          {cursors
            .filter((cursor) => cursor.pageId === page?.id)
            .map((cursor) => (
              <div
                key={cursor.userId}
                className="pointer-events-none absolute z-10 -translate-x-1 -translate-y-1"
                style={{ left: `${(cursor.x / BOARD_WIDTH) * 100}%`, top: `${(cursor.y / BOARD_HEIGHT) * 100}%` }}
              >
                <div className="h-3 w-3 rounded-full border-2 border-white bg-brand-500 shadow" />
                <span className="mt-1 inline-block whitespace-nowrap rounded bg-brand-600 px-1.5 py-0.5 text-[10px] font-medium text-white shadow">
                  {cursor.fullName}
                </span>
              </div>
            ))}
        </div>
      </div>

      <div className="flex items-center gap-2 border-t border-slate-200 bg-white px-3 py-2">
        <span className="text-xs font-medium text-slate-500">Pages</span>
        <div className="flex flex-1 items-center gap-1.5 overflow-x-auto">
          {pages.map((entry, index) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => onSelectPage(entry.id)}
              className={cn(
                'shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                entry.id === activePageId
                  ? 'border-brand-300 bg-brand-50 text-brand-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50',
              )}
            >
              {entry.title ?? `Page ${index + 1}`}
            </button>
          ))}
        </div>
        {canManagePages && (
          <Button variant="outline" size="sm" onClick={onAddPage} leftIcon={<Plus className="h-3.5 w-3.5" aria-hidden />}>
            Add page
          </Button>
        )}
      </div>
    </div>
  );
}
